import type {
  Prisoner,
  PaginatedResponse,
  AuditEvent,
  RecordItem,
  CriminalCase,
  CreatePrisonerDto,
  UpdatePrisonerDto,
  PrisonerProfile,
  PrisonerIdentity,
  PrisonerIdentifier,
  PrisonerBiometrics,
  PrisonerAddress,
  PrisonerContact,
  PrisonerCustodyStatus,
  PrisonerCustodyHistory,
  PrisonerSecurityFlag,
  PrisonerSocialData,
  PrisonerPhoto,
  SecuritySummary,
  AiQueryRequest,
  AiQueryResponse,
  AiSettings,
  UpdateAiSettingsRequest,
  AdminRole,
  PermissionDefinition,
  User,
} from '@/types';
import { getAuthToken } from '@/lib/auth-token';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function toApiPath(endpoint: string): string {
  if (endpoint.startsWith('/api/')) return endpoint;
  if (endpoint.startsWith('/')) return `/api/v1${endpoint}`;
  return `/api/v1/${endpoint}`;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const token = await getAuthToken();
  
  console.log(`[API] ${options.method || 'GET'} ${endpoint}`, { hasToken: !!token });
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401 && !retried) {
      return fetchApi<T>(endpoint, options, true);
    }

    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    console.error(`[API] Error ${response.status}:`, error);
    const message = Array.isArray(error.message)
      ? error.message.join(', ')
      : error.message || `HTTP error ${response.status}`;
    throw new Error(message);
  }

  const data = await response.json();
  console.log(`[API] Response:`, data);
  return data;
}

export const api = {
  get: <T = unknown>(endpoint: string) => fetchApi<T>(toApiPath(endpoint)),
  post: <T = unknown>(endpoint: string, body?: unknown) =>
    fetchApi<T>(toApiPath(endpoint), {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  getCurrentUser: () =>
    fetchApi<{
      id: string;
      username: string;
      email?: string;
      displayName?: string;
      tenantId: string;
      roles: string[];
      permissions: string[];
    }>('/api/v1/auth/me'),

  // Dashboard
  getDashboardStats: () =>
    fetchApi<{
      activePrisoners: number;
      totalPrisoners: number;
      pendingDispositions: number;
      documentsToday: number;
      totalDocuments: number;
      warnings: number;
      recentRecords: Array<{
        id: string;
        title: string;
        type: string;
        updatedAt: string;
        prisoner: {
          id: string;
          firstName: string;
          lastName: string;
          prisonerNumber: string;
        };
      }>;
      recentAdmissions: Array<{
        id: string;
        firstName: string;
        lastName: string;
        prisonerNumber: string;
        admissionDate: string;
      }>;
      upcomingReleases: Array<{
        id: string;
        firstName: string;
        lastName: string;
        prisonerNumber: string;
        releaseDate: string;
      }>;
      documentsByType: Array<{ type: string; count: number }>;
      documentsByNeedle: Array<{ needle: number; count: number }>;
    }>('/api/v1/dashboard/stats'),

  // Prisoners
  getPrisoners: (params: { page: number; pageSize: number; search?: string }) => {
    const searchParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
      ...(params.search ? { search: params.search } : {}),
    });
    return fetchApi<PaginatedResponse<Prisoner>>(
      `/api/v1/prisoners?${searchParams}`
    );
  },

  getPrisoner: (id: string) => fetchApi<Prisoner>(`/api/v1/prisoners/${id}`),

  createPrisoner: (data: CreatePrisonerDto) =>
    fetchApi<Prisoner>('/api/v1/prisoners', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePrisoner: (id: string, data: UpdatePrisonerDto) =>
    fetchApi<Prisoner>(`/api/v1/prisoners/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deletePrisoner: (id: string) =>
    fetchApi<void>(`/api/v1/prisoners/${id}`, {
      method: 'DELETE',
    }),

  // Records
  getRecords: (prisonerId: string, params?: { module?: string; needle?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.module) searchParams.set('module', params.module);
    if (params?.needle) searchParams.set('needle', params.needle.toString());
    return fetchApi<RecordItem[]>(
      `/api/v1/prisoners/${prisonerId}/records?${searchParams}`
    );
  },

  getTimeline: (prisonerId: string) =>
    fetchApi<RecordItem[]>(`/api/v1/prisoners/${prisonerId}/timeline`),

  getNeedleView: (prisonerId: string) =>
    fetchApi<{
      needle1: RecordItem[];
      needle2: { case: CriminalCase; items: RecordItem[] }[];
      needle3: { band: number; items: RecordItem[] }[];
    }>(`/api/v1/prisoners/${prisonerId}/needle-view`),

  // Criminal Cases
  getCriminalCases: (prisonerId: string) =>
    fetchApi<CriminalCase[]>(`/api/v1/prisoners/${prisonerId}/cases`),

  // Audit
  getAuditLogs: (params: { page: number; pageSize: number }) => {
    const searchParams = new URLSearchParams({
      page: params.page.toString(),
      pageSize: params.pageSize.toString(),
    });
    return fetchApi<PaginatedResponse<AuditEvent>>(
      `/api/v1/admin/audit?${searchParams}`
    );
  },

  // Search
  search: (query: string, filters?: Record<string, unknown>) =>
    fetchApi<PaginatedResponse<RecordItem>>('/api/v1/search', {
      method: 'POST',
      body: JSON.stringify({ query, ...filters }),
    }),

  queryAi: (data: AiQueryRequest) =>
    fetchApi<AiQueryResponse>('/api/v1/ai/query', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  reindexAiDocuments: (data?: { prisonerId?: string; documentIds?: string[] }) =>
    fetchApi<{ indexedDocuments: number; indexedChunks: number }>('/api/v1/ai/documents/reindex', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  getAiSettings: () =>
    fetchApi<AiSettings>('/api/v1/settings/ai'),

  updateAiSettings: (data: UpdateAiSettingsRequest) =>
    fetchApi<AiSettings>('/api/v1/settings/ai', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Export
  exportNeedle: (prisonerId: string, params: Record<string, unknown>) =>
    fetchApi<{ jobId: string }>(`/api/v1/prisoners/${prisonerId}/export/needle`, {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  exportModule: (prisonerId: string, params: Record<string, unknown>) =>
    fetchApi<{ jobId: string }>(`/api/v1/prisoners/${prisonerId}/export/module`, {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getExportStatus: (jobId: string) =>
    fetchApi<{ status: string; downloadUrl?: string }>(`/api/v1/exports/${jobId}/status`),

  // ============================================
  // STAMMDATEN (Master Data)
  // ============================================

  // Profil (aggregiert)
  getProfile: (prisonerId: string) =>
    fetchApi<PrisonerProfile>(`/api/v1/prisoners/${prisonerId}/profile`),

  // Identität
  getIdentity: (prisonerId: string) =>
    fetchApi<PrisonerIdentity | null>(`/api/v1/prisoners/${prisonerId}/identity`),

  updateIdentity: (prisonerId: string, data: Partial<PrisonerIdentity>) =>
    fetchApi<PrisonerIdentity>(`/api/v1/prisoners/${prisonerId}/identity`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Identifikatoren
  getIdentifiers: (prisonerId: string) =>
    fetchApi<PrisonerIdentifier[]>(`/api/v1/prisoners/${prisonerId}/identifiers`),

  createIdentifier: (prisonerId: string, data: Partial<PrisonerIdentifier>) =>
    fetchApi<PrisonerIdentifier>(`/api/v1/prisoners/${prisonerId}/identifiers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateIdentifier: (prisonerId: string, id: string, data: Partial<PrisonerIdentifier>) =>
    fetchApi<PrisonerIdentifier>(`/api/v1/prisoners/${prisonerId}/identifiers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteIdentifier: (prisonerId: string, id: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/prisoners/${prisonerId}/identifiers/${id}`, {
      method: 'DELETE',
    }),

  // Biometrie
  getBiometrics: (prisonerId: string) =>
    fetchApi<PrisonerBiometrics | null>(`/api/v1/prisoners/${prisonerId}/biometrics`),

  updateBiometrics: (prisonerId: string, data: Partial<PrisonerBiometrics>) =>
    fetchApi<PrisonerBiometrics>(`/api/v1/prisoners/${prisonerId}/biometrics`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Adressen
  getAddresses: (prisonerId: string) =>
    fetchApi<PrisonerAddress[]>(`/api/v1/prisoners/${prisonerId}/addresses`),

  createAddress: (prisonerId: string, data: Partial<PrisonerAddress>) =>
    fetchApi<PrisonerAddress>(`/api/v1/prisoners/${prisonerId}/addresses`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAddress: (prisonerId: string, id: string, data: Partial<PrisonerAddress>) =>
    fetchApi<PrisonerAddress>(`/api/v1/prisoners/${prisonerId}/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteAddress: (prisonerId: string, id: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/prisoners/${prisonerId}/addresses/${id}`, {
      method: 'DELETE',
    }),

  // Kontakte
  getContacts: (prisonerId: string) =>
    fetchApi<PrisonerContact[]>(`/api/v1/prisoners/${prisonerId}/contacts`),

  createContact: (prisonerId: string, data: Partial<PrisonerContact>) =>
    fetchApi<PrisonerContact>(`/api/v1/prisoners/${prisonerId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateContact: (prisonerId: string, id: string, data: Partial<PrisonerContact>) =>
    fetchApi<PrisonerContact>(`/api/v1/prisoners/${prisonerId}/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteContact: (prisonerId: string, id: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/prisoners/${prisonerId}/contacts/${id}`, {
      method: 'DELETE',
    }),

  // Vollzugsstatus
  getCustodyStatus: (prisonerId: string) =>
    fetchApi<PrisonerCustodyStatus | null>(`/api/v1/prisoners/${prisonerId}/custody`),

  updateCustodyStatus: (prisonerId: string, data: Partial<PrisonerCustodyStatus>) =>
    fetchApi<PrisonerCustodyStatus>(`/api/v1/prisoners/${prisonerId}/custody`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getCustodyHistory: (prisonerId: string) =>
    fetchApi<PrisonerCustodyHistory[]>(`/api/v1/prisoners/${prisonerId}/custody/history`),

  createCustodyHistory: (prisonerId: string, data: Partial<PrisonerCustodyHistory>) =>
    fetchApi<PrisonerCustodyHistory>(`/api/v1/prisoners/${prisonerId}/custody/history`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Sicherheits-Flags
  getSecurityFlags: (prisonerId: string) =>
    fetchApi<SecuritySummary>(`/api/v1/prisoners/${prisonerId}/security`),

  createSecurityFlag: (prisonerId: string, data: Partial<PrisonerSecurityFlag>) =>
    fetchApi<PrisonerSecurityFlag>(`/api/v1/prisoners/${prisonerId}/security`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSecurityFlag: (prisonerId: string, id: string, data: Partial<PrisonerSecurityFlag>) =>
    fetchApi<PrisonerSecurityFlag>(`/api/v1/prisoners/${prisonerId}/security/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  clearSecurityFlag: (prisonerId: string, id: string, reason?: string) =>
    fetchApi<PrisonerSecurityFlag>(`/api/v1/prisoners/${prisonerId}/security/${id}/clear`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Soziale Daten
  getSocialData: (prisonerId: string) =>
    fetchApi<PrisonerSocialData | null>(`/api/v1/prisoners/${prisonerId}/social`),

  updateSocialData: (prisonerId: string, data: Partial<PrisonerSocialData>) =>
    fetchApi<PrisonerSocialData>(`/api/v1/prisoners/${prisonerId}/social`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Fotos
  getPhotos: (prisonerId: string) =>
    fetchApi<PrisonerPhoto[]>(`/api/v1/prisoners/${prisonerId}/photos`),

  uploadPhoto: async (prisonerId: string, file: File, metadata?: { isPrimary?: boolean; takenAt?: string; description?: string }) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.isPrimary) formData.append('isPrimary', 'true');
    if (metadata?.takenAt) formData.append('takenAt', metadata.takenAt);
    if (metadata?.description) formData.append('description', metadata.description);
    
    const response = await fetch(`${API_URL}/api/v1/prisoners/${prisonerId}/photos`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || `HTTP error ${response.status}`);
    }
    
    return response.json() as Promise<PrisonerPhoto>;
  },

  updatePhoto: (prisonerId: string, id: string, data: { isPrimary?: boolean; takenAt?: string; description?: string }) =>
    fetchApi<PrisonerPhoto>(`/api/v1/prisoners/${prisonerId}/photos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePhoto: (prisonerId: string, id: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/prisoners/${prisonerId}/photos/${id}`, {
      method: 'DELETE',
    }),

  // Users
  getUsers: () =>
    fetchApi<User[]>('/api/v1/users'),

  getUser: (id: string) => fetchApi<User>(`/api/v1/users/${id}`),

  getRoles: () =>
    fetchApi<AdminRole[]>('/api/v1/users/roles'),

  getPermissions: () =>
    fetchApi<PermissionDefinition[]>('/api/v1/users/permissions'),

  createUser: (data: {
    username: string;
    email: string;
    displayName?: string;
    password: string;
    roles?: string[];
    roleIds?: string[];
    enabled?: boolean;
  }) =>
    fetchApi<User>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUser: (
    id: string,
    data: {
      email?: string;
      displayName?: string;
      roles?: string[];
      roleIds?: string[];
      isActive?: boolean;
    }
  ) =>
    fetchApi<User>(`/api/v1/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateUserRoles: (id: string, roleIds: string[]) =>
    fetchApi<User>(`/api/v1/users/${id}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roleIds }),
    }),

  updateUserPermissions: (id: string, grant: string[], deny: string[]) =>
    fetchApi<User>(`/api/v1/users/${id}/permissions`, {
      method: 'PATCH',
      body: JSON.stringify({ grant, deny }),
    }),

  setUserEnabled: (id: string, enabled: boolean) =>
    fetchApi<User>(`/api/v1/users/${id}/enabled`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  deleteUser: (id: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/users/${id}`, {
      method: 'DELETE',
    }),

  createRole: (data: {
    name: string;
    description?: string;
    keycloakRoleName?: string;
    permissionKeys: string[];
  }) =>
    fetchApi<AdminRole[]>('/api/v1/users/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRole: (
    roleId: string,
    data: {
      name: string;
      description?: string;
      keycloakRoleName?: string;
      permissionKeys: string[];
      system?: boolean;
    }
  ) =>
    fetchApi<AdminRole[]>(`/api/v1/users/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteRole: (roleId: string) =>
    fetchApi<{ success: boolean }>(`/api/v1/users/roles/${roleId}`, {
      method: 'DELETE',
    }),
};
